import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import useValidationSchema, { FieldConfig } from "../../components/Validations";
import Toast from "../../helpers/Toast";
import { CounterTypeCreateDTO } from '../../models/Counter/CounterTypeCreateDTO';
import { CounterTypeService } from "../../services/CounterTypeService";

export default function CounterTypeCreate() {
  const intl = useIntl();
  const counterService = new CounterTypeService();
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const [counterType, setCounterType] = useState<{
    code: string;
    name: string;
    unit?: string;
    description?: string;
  }>({
    code: "",
    name: "",
    unit: "",
    description: ""
  });

  const navigate = useNavigate();

  const fields: FieldConfig[] = [
    ['code', 'string', 'Screens.CounterTypes.Create.Validation.CodeRequired'],
    ['name', 'string', 'Screens.CounterTypes.Create.Validation.NameRequired']
  ];

  const schemaCounterTypeValidation = useValidationSchema(fields);

  const createCounterType = async () => {
    setLoadingCreate(true);

    const createCounterTypeDTO: CounterTypeCreateDTO = {
      code: counterType.code,
      name: counterType.name,
      unit: counterType.unit && counterType.unit.trim() !== "" ? counterType.unit : undefined,
      description: counterType.description && counterType.description.trim() !== "" ? counterType.description : undefined
    };

    const { error } = schemaCounterTypeValidation.validate(createCounterTypeDTO);

    if (error) {
      Toast.Show("error", error.details[0].message);
      setLoadingCreate(false);
      return;
    }

    const response = await counterService.CreateCounter(createCounterTypeDTO);

    if (response.success !== true) {
      Toast.Show("error", response.message);
      setLoadingCreate(false);
      return;
    }

    Toast.Show("success", response.message);
    setLoadingCreate(false);
    navigate(-1);
  };

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <PageTitle title={intl.formatMessage({ id: "Screens.CounterTypes.Create.Title" })} allowGoBack={true} />
        </div>
        <div className="pageBody">
          <label style={{ margin: "0px", padding: "13px" }}>
            <b>{intl.formatMessage({ id: "Screens.CounterTypes.Create.SubTitle" })}</b>
          </label>
          <Row
            style={{
              margin: "0px",
              padding: "0px",
              alignItems: "flex-end",
              alignContent: "flex-end",
            }}
          >
            <Row>
              <InputComponent
                xl={4}
                lg={6}
                md={12}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={counterType.code}
                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Code" })}
                readOnly={false}
                onChange={(t: string) => {
                  setCounterType({ ...counterType, code: t })
                }}
                required={true}
              />
            </Row>
            <Row>
              <InputComponent
                xl={4}
                lg={6}
                md={12}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={counterType.name}
                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Name" })}
                readOnly={false}
                onChange={(t: string) => {
                  setCounterType({ ...counterType, name: t })
                }}
                required={true}
              />
            </Row>
            <Row>
              <InputComponent
                xl={4}
                lg={6}
                md={12}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={counterType.unit}
                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Unit" })}
                readOnly={false}
                onChange={(t: string) => {
                  setCounterType({ ...counterType, unit: t })
                }}
              />
            </Row>
            <Row>
              <InputComponent
                xl={4}
                lg={6}
                md={12}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"textArea"}
                value={counterType.description}
                label={intl.formatMessage({ id: "Screens.CounterTypes.Table.Description" })}
                readOnly={false}
                onChange={(t: string) => {
                  setCounterType({ ...counterType, description: t })
                }}
              />
            </Row>
          </Row>
          <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
            <Col xl={1} lg={1} md={1} sm={1} xs={1}>
              <ButtonComponent
                disabled={loadingCreate}
                loading={loadingCreate}
                text={intl.formatMessage({ id: "Common.Buttons.Save" })}
                onClick={createCounterType}
              />
            </Col>
          </Row>
        </div>
      </div>
    </Layout>
  );
}
